import React, {useState} from 'react';
import {FlatList, Pressable, ScrollView, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {useClubLeaderboard} from '@/Hooks/ClubsApi/useClubsQueries';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {LeaderboardRow} from '@/Hooks/ClubsApi/types';
import {LeaderboardSkeleton} from './ClubRoomSkeletons';
import FormulaInfo from './FormulaInfo';

// Top-3 medal tints (design leaderboard VM: ['#FACF47','#CACACA','#E0A96E']).
const rankColor = (r: number) =>
  r === 1 ? '#D1AB41' : r === 2 ? '#A8A29A' : r === 3 ? '#B5794A' : '#888280';

const AVATAR_COLORS = ['#3DAB9E', '#E17846', '#67B3E1', '#937ADB', '#D1AB41'];
const avatarColor = (name: string) =>
  AVATAR_COLORS[Math.abs(name.charCodeAt(0)) % AVATAR_COLORS.length];

const TrendBadge = ({ trend, delta }: { trend: string; delta: number }) => {
  if (trend === "new" || delta === 0) return null;
  const up = trend === "up";
  return (
    <CCText fontSize={12} color={up ? "#1E9E63" : "#C8433B"} fontWeight="600">
      {up ? "▲" : "▼"}{Math.abs(delta)}
    </CCText>
  );
};

export default function LeaderboardTab({
  clubId,
  variant = 'mobile',
  limit,
}: {
  clubId: number;
  variant?: 'mobile' | 'rail' | 'overview';
  limit?: number;
}) {
  const [period, setPeriod] = useState<'daily' | 'weekly'>(
    variant === 'overview' ? 'weekly' : 'daily',
  );
  const {data, isLoading} = useClubLeaderboard(clubId, period);
  const myId = useLoggedInUserStore(s => s.userId);
  const isRail = variant === 'rail';
  const isOverview = variant === 'overview';

  // Slice rows to limit when provided
  const displayRows = limit && data?.rows ? data.rows.slice(0, limit) : data?.rows ?? [];

  const Toggle = () => (
    <View
      style={
        isRail
          ? {
              flexDirection: 'row',
              backgroundColor: '#EDE6D2',
              borderRadius: 8,
              padding: 3,
            }
          : {flexDirection: 'row', gap: 6}
      }>
      {(['daily', 'weekly'] as const).map(p => {
        const active = period === p;
        if (isRail) {
          // Segmented control inside the rail card header (design line 885).
          return (
            <Pressable
              key={p}
              testID={`lb-period-${p}`}
              onPress={() => setPeriod(p)}
              style={{
                paddingVertical: 5,
                paddingHorizontal: 12,
                borderRadius: 6,
                backgroundColor: active ? '#3DAB9E' : 'transparent',
              }}>
              <CCText
                style={{
                  fontWeight: '600',
                  color: active ? '#fff' : '#6B584D',
                  fontSize: 12,
                }}>
                {p === 'daily' ? 'Today' : 'Week'}
              </CCText>
            </Pressable>
          );
        }
        // Mobile pill row (design line 411).
        return (
          <Pressable
            key={p}
            testID={`lb-period-${p}`}
            onPress={() => setPeriod(p)}
            style={{
              paddingVertical: 7,
              paddingHorizontal: 14,
              borderRadius: 999,
              backgroundColor: active ? '#3DAB9E' : '#fff',
              borderWidth: active ? 0 : 1,
              borderColor: 'rgba(38,35,34,0.12)',
            }}>
            <CCText
              style={{
                fontWeight: '600',
                color: active ? '#fff' : '#6B584D',
                fontSize: 13,
              }}>
              {p === 'daily' ? 'Today' : 'This Week'}
            </CCText>
          </Pressable>
        );
      })}
    </View>
  );

  const renderRow = (item: LeaderboardRow) => {
    const isMe = String(item.user_id) === String(myId);
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: isRail ? 9 : 11,
          paddingVertical: isRail ? 7 : 8,
          paddingHorizontal: isRail ? 9 : 11,
          borderRadius: 12,
          // Me-row highlight (design: #FDF1C8 bg + 1.5px #FACF47 border).
          backgroundColor: isMe ? '#FDF1C8' : '#fff',
          borderWidth: isMe ? 1.5 : 0,
          borderColor: isMe ? '#FACF47' : 'transparent',
          ...(isMe
            ? {}
            : {
                shadowColor: '#4D3F37',
                shadowOffset: {width: 0, height: 1},
                shadowOpacity: 0.05,
                shadowRadius: 2,
                elevation: 1,
              }),
        }}>
        <CCText
          style={{
            width: isRail ? 26 : 26,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: '700',
            color: rankColor(item.rank),
          }}>
          {item.rank}
        </CCText>
        <View
          style={{
            width: isRail ? 26 : 32,
            height: isRail ? 26 : 32,
            borderRadius: 16,
            backgroundColor: avatarColor(item.username),
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <CCText
            style={{
              color: '#fff',
              fontWeight: '600',
              fontSize: isRail ? 10.5 : 12,
            }}>
            {item.username.charAt(0).toUpperCase()}
          </CCText>
        </View>
        <View style={{flex: 1}}>
          <CCText
            numberOfLines={1}
            style={{
              fontSize: isRail ? 13 : 14.5,
              fontWeight: isMe ? '700' : '600',
              color: '#262322',
            }}>
            {isMe ? 'You' : item.name || item.username}
          </CCText>
          {/* Username on the second line (smaller). "You" always keeps its real
              username below; others show it only when a name is present. */}
          {isMe || item.name ? (
            <CCText
              numberOfLines={1}
              style={{
                fontSize: isRail ? 10.5 : 11.5,
                color: '#888280',
                marginTop: 1,
              }}>
              {item.username}
            </CCText>
          ) : null}
        </View>
        <View style={{alignItems: 'center', gap: 4}}>
          <CCText
            style={{
              fontSize: isRail ? 13 : 14,
              fontWeight: '700',
              color: '#4D3F37',
            }}>
            {item.points}
            {isRail ? null : (
              <CCText style={{fontSize: 11, fontWeight: '500', color: '#888280'}}>
                {' '}
                pts
              </CCText>
            )}
          </CCText>
          <TrendBadge trend={item.trend} delta={item.delta} />
        </View>
      </View>
    );
  };

  if (isRail || isOverview) {
    // Rail/overview: header row (title + toggle) then a plain (non-scrolling) column.
    return (
      <View>
        {!isOverview ? (
          <View
            style={{
              position: 'relative',
              zIndex: 100,
              elevation: 20,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <CCText style={{fontSize: 13, fontWeight: '700', color: '#262322'}}>
                Leaderboard
              </CCText>
              <FormulaInfo align="left" />
            </View>
            <Toggle />
          </View>
        ) : null}
        {isLoading ? (
          <LeaderboardSkeleton variant="rail" />
        ) : (
          // Show ~10 rows then scroll inside. maxHeight is sized so the 10th row is
          // partially cut, hinting there's more to scroll. (rail row ≈ 40px + 5 gap.)
          <ScrollView
            testID="club-leaderboard-scroll"
            scrollEnabled={!isOverview}
            style={
              isOverview
                ? {}
                : {
                    position: 'relative',
                    zIndex: 0,
                    maxHeight: 430,
                  }
            }
            contentContainerStyle={{gap: 5}}
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled>
            {displayRows.map(item => (
              <View key={String(item.user_id)}>{renderRow(item)}</View>
            ))}
          </ScrollView>
        )}
      </View>
    );
  }

  return (
    <View style={{flex: 1, position: 'relative'}}>
      <View
        testID="leaderboard-mobile-header"
        style={{
          position: 'relative',
          zIndex: 1000,
          elevation: 50,
          overflow: 'visible',
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 14,
          paddingTop: 12,
          paddingBottom: 6,
        }}>
        <Toggle />
        <View style={{flex: 1}} />
        <FormulaInfo />
      </View>
      {isLoading ? (
        <LeaderboardSkeleton />
      ) : (
        <FlatList
          testID="club-leaderboard-scroll"
          style={{position: 'relative', zIndex: 0}}
          data={displayRows}
          keyExtractor={r => String(r.user_id)}
          contentContainerStyle={{paddingHorizontal: 14, paddingTop: 4, gap: 6}}
          showsVerticalScrollIndicator={false}
          renderItem={({item}) => renderRow(item)}
        />
      )}
    </View>
  );
}
